import React, { useEffect, useState } from 'react'
import {
  ChevronDown,
  ChevronRight,
  AlertCircle,
  TestTube2Icon,
} from 'lucide-react'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { lmRunLogApiClient } from '@/lib/api/lm-run-log.client'
import { decisionLogApiClient } from '@/lib/api/decision-log.client'
import type { DecisionLog, LmRunLog } from '@super/shared-types'
import { DECISION_DEFINITIONS, EvalPromptType } from '@super/shared-types'
import { PROMPT_TEMPLATE_MAP } from '@super/eval-framework'
import { PromoteToEvalModal } from './PromoteToEvalModal'
import { Heading } from '@/components/ui/typography'

interface SessionDetailsDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sessionId: string | null
  companyId: string | undefined
}

const PROMPT_TYPE_LABELS: Record<string, string> = {
  'property-matching': 'Property Matching',
  'group-detection': 'Group Detection',
  'scenario-classification': 'Scenario Classification',
  'scenario-variable-extraction': 'Variable Extraction',
  'handoff-workflow-selection': 'Workflow Selection',
  'sms-handoff-workflow-selection': 'Workflow Selection',
  'action-group-selection': 'Action Group Selection',
  'sms-action-group-selection': 'Action Group Selection',
  'handoff-trigger-eval': 'Handoff Trigger Eval',
}

const EVAL_PROMPT_TYPES = new Set<string>(Object.values(EvalPromptType))

function JsonBlock({ data }: { data: Record<string, unknown> }) {
  return (
    <pre className="text-[11px] bg-[#0a0a0a] text-success-dim rounded p-3 overflow-x-auto leading-relaxed font-mono whitespace-pre-wrap break-all">
      {JSON.stringify(data, null, 2)}
    </pre>
  )
}

function RunStep({
  run,
  index,
  total,
  companyId,
}: {
  run: LmRunLog
  index: number
  total: number
  companyId: string
}) {
  const [inputOpen, setInputOpen] = useState(false)
  const [outputOpen, setOutputOpen] = useState(false)
  const [promoteOpen, setPromoteOpen] = useState(false)

  const label = PROMPT_TYPE_LABELS[run.promptType] ?? run.promptType
  const canPromote =
    EVAL_PROMPT_TYPES.has(run.promptType) && run.source !== 'langchain'

  const dotColor = (() => {
    if (run.error) return 'bg-error'
    switch (run.source) {
      case 'langchain':
        return 'bg-channel-sms'
      default:
        return 'bg-muted-foreground'
    }
  })()
  const timestamp = new Date(run.createdAt).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  })
  const durationSec =
    run.durationMs != null ? `${(run.durationMs / 1000).toFixed(2)}s` : null
  const totalTokens =
    run.promptTokens != null && run.completionTokens != null
      ? run.promptTokens + run.completionTokens
      : null
  const costStr = run.costUsd != null ? `$${run.costUsd.toFixed(5)}` : null

  const isLast = index === total - 1

  const highlights = PROMPT_TEMPLATE_MAP[run.promptType]?.outputHighlights
  const output = run.actualOutput as Record<string, unknown> | null

  return (
    <div className="flex gap-3">
      {/* Timestamp - left of spine */}
      <div className="w-20 flex-shrink-0 text-right pt-0.5 -mt-1">
        <span className="text-xs text-muted-foreground/60 font-mono">
          {timestamp}
        </span>
      </div>

      {/* Timeline spine */}
      <div className="flex flex-col items-center flex-shrink-0">
        <div
          className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${dotColor}`}
        />
        {!isLast && <div className="w-px flex-1 bg-border mt-1" />}
      </div>

      {/* Content */}
      <div className={`flex-1 pb-5 ${isLast ? '' : ''}`}>
        {/* Header row */}
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-foreground ring-1 ring-inset ring-border">
              {label}
            </span>
            {run.source === 'langchain' && (
              <span className="inline-flex items-center rounded-md bg-channel-sms-subtle px-2 py-0.5 text-xs font-medium text-channel-sms ring-1 ring-inset ring-channel-sms-border">
                LangChain
              </span>
            )}
            {run.error && (
              <span className="inline-flex items-center gap-1 rounded-md bg-error-subtle px-2 py-0.5 text-xs font-medium text-error ring-1 ring-inset ring-error-border">
                <AlertCircle className="h-3 w-3" />
                Error
              </span>
            )}
          </div>
          {canPromote && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => setPromoteOpen(true)}
            >
              <TestTube2Icon className="h-3 w-3 mr-1" />
              Eval
            </Button>
          )}
        </div>

        {/* Metadata row */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2 flex-wrap">
          <span className="font-mono text-muted-foreground">{run.model}</span>
          {durationSec && (
            <>
              <span className="text-border">·</span>
              <span>{durationSec}</span>
            </>
          )}
          {totalTokens != null && (
            <>
              <span className="text-border">·</span>
              <span>{totalTokens.toLocaleString()} tokens</span>
            </>
          )}
          {costStr && (
            <>
              <span className="text-border">·</span>
              <span>{costStr}</span>
            </>
          )}
        </div>

        {/* Highlight block */}
        {highlights && output && (
          <div className="mb-2 rounded-md bg-slate-50 dark:bg-muted border border-slate-200 dark:border-border px-3 py-2 flex flex-col gap-1.5">
            {highlights.fields.map(({ key, label }) => {
              const val = output[key]
              if (val == null) return null
              const str = typeof val === 'string' ? val : JSON.stringify(val)
              const isLong = str.length > 60
              return (
                <div
                  key={key}
                  className="flex flex-col gap-0.5 'flex items-baseline gap-1 mb-2"
                >
                  <div className="text-xs font-medium text-slate-400 dark:text-muted-foreground uppercase tracking-wide shrink-0">
                    {label}
                  </div>
                  <div
                    className={`text-xs text-slate-700 dark:text-foreground ${isLong && 'leading-relaxed'}`}
                  >
                    {str}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Error message */}
        {run.error && (
          <div className="mb-2 text-xs text-error bg-error-subtle rounded px-3 py-2 font-mono">
            {run.error}
          </div>
        )}

        {/* Input */}
        {(run.inputContext || (run.source !== 'langchain' && run.content)) && (
          <div className="mb-1">
            <button
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mb-1"
              onClick={() => setInputOpen(v => !v)}
            >
              {inputOpen ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              Input
            </button>
            {inputOpen && (
              <>
                {run.inputContext && <JsonBlock data={run.inputContext} />}
                {run.source !== 'langchain' && run.content && (
                  <pre className="text-[11px] bg-[#0a0a0a] text-success-dim rounded p-3 overflow-x-auto leading-relaxed font-mono whitespace-pre-wrap break-all">
                    {run.content}
                  </pre>
                )}
              </>
            )}
          </div>
        )}

        {/* Output */}
        {run.actualOutput && (
          <div>
            <button
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mb-1"
              onClick={() => setOutputOpen(v => !v)}
            >
              {outputOpen ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              Output
            </button>
            {outputOpen && <JsonBlock data={run.actualOutput} />}
          </div>
        )}

        {canPromote && (
          <PromoteToEvalModal
            open={promoteOpen}
            onOpenChange={setPromoteOpen}
            runLog={run}
            companyId={companyId}
          />
        )}
      </div>
    </div>
  )
}

const REASON_DOT_COLOR: Record<string, string> = {
  matched: 'bg-success',
  confirmed: 'bg-success',
  ambiguous: 'bg-warning',
  near_miss: 'bg-warning',
  no_match: 'bg-muted-foreground',
  agent_missing: 'bg-warning',
  error: 'bg-error',
}

function DecisionStep({
  decision,
  index,
  total,
}: {
  decision: DecisionLog
  index: number
  total: number
}) {
  const [inputsOpen, setInputsOpen] = useState(false)
  const [outcomeOpen, setOutcomeOpen] = useState(false)

  const definition =
    DECISION_DEFINITIONS[
      decision.decisionType as keyof typeof DECISION_DEFINITIONS
    ]
  const label = definition?.displayName ?? decision.decisionType
  const dotColor =
    REASON_DOT_COLOR[decision.reason ?? ''] ?? 'bg-muted-foreground'

  const timestamp = new Date(decision.createdAt).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  })
  const durationStr =
    decision.durationMs != null ? `${decision.durationMs}ms` : null

  const isLast = index === total - 1

  return (
    <div className="flex gap-3">
      <div className="w-20 flex-shrink-0 text-xs text-muted-foreground text-right pt-1.5 font-mono">
        {timestamp}
      </div>
      <div className="flex flex-col items-center flex-shrink-0">
        <div
          className={`w-2 h-2 rounded-full mt-2 ${dotColor} flex-shrink-0`}
        />
        {!isLast && (
          <div className="w-px flex-1 bg-gray-200 dark:bg-border mt-1" />
        )}
      </div>
      <div className="flex-1 pb-5">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium text-foreground">{label}</span>
          {decision.reason && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
              {decision.reason}
            </span>
          )}
          {decision.error && <AlertCircle className="h-3 w-3 text-error" />}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
          {durationStr && <span>{durationStr}</span>}
          {decision.source && (
            <>
              <span className="w-1 h-1 rounded-full bg-muted-foreground/40" />
              <span className="font-mono">{decision.source}</span>
            </>
          )}
        </div>

        {decision.inputs && (
          <div className="mb-2">
            <button
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mb-1"
              onClick={() => setInputsOpen(v => !v)}
            >
              {inputsOpen ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              Inputs
            </button>
            {inputsOpen && <JsonBlock data={decision.inputs} />}
          </div>
        )}
        {decision.outcome && (
          <div>
            <button
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mb-1"
              onClick={() => setOutcomeOpen(v => !v)}
            >
              {outcomeOpen ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              Outcome
            </button>
            {outcomeOpen && <JsonBlock data={decision.outcome} />}
          </div>
        )}
      </div>
    </div>
  )
}

type TimelineEntry =
  | { kind: 'run'; log: LmRunLog }
  | { kind: 'decision'; log: DecisionLog }

export const SessionDetailsDrawer: React.FC<SessionDetailsDrawerProps> = ({
  open,
  onOpenChange,
  sessionId,
  companyId,
}) => {
  const [runs, setRuns] = useState<LmRunLog[]>([])
  const [decisions, setDecisions] = useState<DecisionLog[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !sessionId || !companyId) return

    setLoading(true)
    setError(null)

    Promise.all([
      lmRunLogApiClient.getBySession(sessionId, companyId),
      decisionLogApiClient.getBySession(sessionId, companyId),
    ])
      .then(([runRes, decisionRes]) => {
        setRuns(runRes.data)
        setDecisions(decisionRes.data)
      })
      .catch(() => setError('Failed to load session details.'))
      .finally(() => setLoading(false))
  }, [open, sessionId, companyId])

  const timeline: TimelineEntry[] = React.useMemo(() => {
    const entries: TimelineEntry[] = [
      ...runs.map<TimelineEntry>(log => ({ kind: 'run', log })),
      ...decisions.map<TimelineEntry>(log => ({ kind: 'decision', log })),
    ]
    entries.sort(
      (a, b) =>
        new Date(a.log.createdAt).getTime() -
        new Date(b.log.createdAt).getTime(),
    )
    return entries
  }, [runs, decisions])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto p-0"
      >
        <div className="flex flex-col h-full bg-white dark:bg-background">
          <div className="px-8 pt-8 pb-5 border-b border-gray-100 dark:border-border">
            <div className="flex items-center gap-3">
              <div>
                <Heading
                  variant="section-title"
                  className="text-lg font-semibold text-foreground leading-snug"
                >
                  Session Details
                </Heading>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-8 py-6">
            {loading && (
              <div className="flex flex-col gap-0">
                {[88, 64, 72, 56].map((labelW, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="w-20 flex-shrink-0 flex justify-end pt-1.5">
                      <div className="w-14 h-3 bg-gray-200 dark:bg-muted rounded animate-pulse" />
                    </div>
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div className="w-2 h-2 rounded-full mt-2 bg-gray-200 dark:bg-muted animate-pulse flex-shrink-0" />
                      {i < 3 && (
                        <div className="w-px flex-1 bg-gray-100 dark:bg-border mt-1" />
                      )}
                    </div>
                    <div className="flex-1 pb-5">
                      <div className="flex items-center gap-2 mb-2">
                        <div
                          className="h-5 rounded-md bg-gray-200 dark:bg-muted animate-pulse"
                          style={{ width: labelW }}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-3 bg-gray-100 dark:bg-muted/60 rounded animate-pulse" />
                        <div className="w-1 h-1 rounded-full bg-gray-200 dark:bg-muted" />
                        <div className="w-10 h-3 bg-gray-100 dark:bg-muted/60 rounded animate-pulse" />
                        <div className="w-1 h-1 rounded-full bg-gray-200 dark:bg-muted" />
                        <div className="w-16 h-3 bg-gray-100 dark:bg-muted/60 rounded animate-pulse" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {error && <p className="text-sm text-error-dim">{error}</p>}
            {!loading && !error && timeline.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No events recorded for this session.
              </p>
            )}
            {!loading && !error && timeline.length > 0 && (
              <div>
                {timeline.map((entry, index, arr) =>
                  entry.kind === 'run' ? (
                    <RunStep
                      key={`run-${entry.log.id}`}
                      run={entry.log}
                      index={index}
                      total={arr.length}
                      companyId={companyId ?? ''}
                    />
                  ) : (
                    <DecisionStep
                      key={`decision-${entry.log.id}`}
                      decision={entry.log}
                      index={index}
                      total={arr.length}
                    />
                  ),
                )}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
